import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { toast } from 'react-toastify';
import { initializeAuth, getAuthToken } from '../admin/jscontrol/apiClient';
import {
  addToCartApi,
  addBundleToCartApi,
  clearCartApi,
  checkoutCartApi,
  clearPendingCartItem,
  ensureLoggedInUserId,
  fetchCartByUserId,
  getLoggedInUserId,
  getPendingCartItem,
  isCartUserLoggedIn,
  removeCartItemApi,
  savePendingCartItem,
  updateCartItemApi,
} from '../utils/cartApi';

const CartContext = createContext(null);

export function CartProvider({ children, onRequireLogin, onCartOpen }) {
  const [cartItems, setCartItems] = useState([]);
  const [cartId, setCartId] = useState(null);
  const [cartStatus, setCartStatus] = useState(null);
  const [loading, setLoading] = useState(false);
  const [actionId, setActionId] = useState(null);
  const [checkingOut, setCheckingOut] = useState(false);

  const refreshCart = useCallback(async ({ showLoading = true } = {}) => {
    if (!getAuthToken()) {
      setCartItems([]);
      setCartId(null);
      setCartStatus(null);
      return;
    }
    const userId = await ensureLoggedInUserId();
    if (!userId) {
      setCartItems([]);
      setCartId(null);
      setCartStatus(null);
      return;
    }

    if (showLoading) setLoading(true);
    try {
      const cart = await fetchCartByUserId(userId);
      setCartItems(cart.items);
      setCartId(cart.cartId);
      setCartStatus(cart.cartStatus ?? null);
    } catch {
      setCartItems([]);
      setCartId(null);
      setCartStatus(null);
    } finally {
      if (showLoading) setLoading(false);
    }
  }, []);

  useEffect(() => {
    initializeAuth().then(() => refreshCart());
  }, [refreshCart]);

  const itemCount = useMemo(
    () => cartItems.reduce((sum, item) => sum + (item.quantity || 0), 0),
    [cartItems]
  );

  const processPendingAddToCart = useCallback(async () => {
    const pending = getPendingCartItem();
    if (!pending) return false;

    const userId = await ensureLoggedInUserId();
    if (!userId) return false;

    setActionId(`add-${pending.id}`);
    try {
      await addToCartApi({
        userId,
        productId: pending.id,
        quantity: pending.quantity,
        unitPrice: pending.price,
        createdBy: userId,
      });
      clearPendingCartItem();
      await refreshCart({ showLoading: false });
      onCartOpen?.();
      const label = pending.name || 'Item';
      toast.success(`${label} added to cart!`);
      return true;
    } catch (err) {
      toast.error(err.message || 'Could not add saved item to cart.');
      return false;
    } finally {
      setActionId(null);
    }
  }, [onCartOpen, refreshCart]);

  const addToCart = useCallback(async (product) => {
    if (!isCartUserLoggedIn()) {
      savePendingCartItem(product);
      toast.warning('Please login. Your item will be added to cart after login.');
      onRequireLogin?.();
      return false;
    }

    const userId = await ensureLoggedInUserId();
    if (!userId) {
      savePendingCartItem(product);
      toast.warning('Please login. Your item will be added to cart after login.');
      onRequireLogin?.();
      return false;
    }

    const productId = Number(product.id);
    const unitPrice = Number(product.price ?? 0);

    if (!productId) {
      toast.error('Invalid product.');
      return false;
    }

    setActionId(`add-${productId}`);
    try {
      await addToCartApi({
        userId,
        productId,
        quantity: 1,
        unitPrice,
        createdBy: userId,
      });
      await refreshCart({ showLoading: false });
      onCartOpen?.();
      toast.success('Added to cart!');
      return true;
    } catch (err) {
      toast.error(err.message || 'Could not add to cart.');
      return false;
    } finally {
      setActionId(null);
    }
  }, [onRequireLogin, onCartOpen, refreshCart]);

  const addBundleToCart = useCallback(async (bundle) => {
    if (!bundle?.bundleProductId || !bundle?.products?.length) return false;

    if (!isCartUserLoggedIn()) {
      toast.warning('Please login to add bundle to cart.');
      onRequireLogin?.();
      return false;
    }

    const userId = await ensureLoggedInUserId();
    if (!userId) {
      toast.warning('Please login to add bundle to cart.');
      onRequireLogin?.();
      return false;
    }

    setActionId(`add-bundle-${bundle.bundleProductId}`);
    try {
      await addBundleToCartApi({
        userId,
        bundleProductId: bundle.bundleProductId,
        bundlePrice: bundle.price,
        products: bundle.products,
        createdBy: userId,
      });
      await refreshCart({ showLoading: false });
      onCartOpen?.();
      toast.success('Bundle added to cart!');
      return true;
    } catch (err) {
      toast.error(err.message || 'Could not add bundle to cart.');
      return false;
    } finally {
      setActionId(null);
    }
  }, [onRequireLogin, onCartOpen, refreshCart]);

  const removeFromCart = useCallback(async (cartItemId) => {
    if (!cartItemId) return;

    setActionId(`remove-${cartItemId}`);
    try {
      await removeCartItemApi(cartItemId);
      await refreshCart({ showLoading: false });
    } catch (err) {
      toast.error(err.message || 'Could not remove item.');
    } finally {
      setActionId(null);
    }
  }, [refreshCart]);

  const updateQuantity = useCallback(async (cartItemId, newQuantity, item) => {
    if (!cartItemId) return;

    if (item?.isBundleItem && newQuantity !== 1) {
      toast.info('Bundle quantity is fixed. Add the bundle again for another set.');
      return;
    }

    if (newQuantity < 1) {
      await removeFromCart(cartItemId);
      return;
    }

    const userId = await ensureLoggedInUserId();

    setActionId(`qty-${cartItemId}`);
    try {
      await updateCartItemApi(cartItemId, newQuantity, userId);
      await refreshCart({ showLoading: false });
    } catch (err) {
      toast.error(err.message || 'Could not update quantity.');
    } finally {
      setActionId(null);
    }
  }, [removeFromCart, refreshCart]);

  const clearCart = useCallback(async () => {
    const userId = getLoggedInUserId();
    if (!userId) {
      setCartItems([]);
      return;
    }

    try {
      await clearCartApi(userId);
    } catch {
      /* still clear local state on logout */
    }
    setCartItems([]);
    setCartId(null);
    setCartStatus(null);
  }, []);

  const checkoutCart = useCallback(async () => {
    if (!isCartUserLoggedIn()) {
      toast.warning('Please login to checkout.');
      onRequireLogin?.();
      return false;
    }

    const userId = await ensureLoggedInUserId();
    if (!userId) {
      toast.error('Could not identify your account. Please login again.');
      onRequireLogin?.();
      return false;
    }

    setCheckingOut(true);
    try {
      const result = await checkoutCartApi(userId, userId);
      await refreshCart({ showLoading: false });
      toast.success(`Checkout complete! Cart #${result.processedCartId} is now Processed.`);
      return result;
    } catch (err) {
      toast.error(err.message || 'Checkout failed.');
      return null;
    } finally {
      setCheckingOut(false);
    }
  }, [onRequireLogin, refreshCart]);

  const value = useMemo(
    () => ({
      cartItems,
      cartId,
      cartStatus,
      itemCount,
      loading,
      actionId,
      checkingOut,
      addToCart,
      addBundleToCart,
      removeFromCart,
      updateQuantity,
      refreshCart,
      clearCart,
      checkoutCart,
      processPendingAddToCart,
    }),
    [
      cartItems,
      cartId,
      cartStatus,
      itemCount,
      loading,
      actionId,
      checkingOut,
      addToCart,
      addBundleToCart,
      removeFromCart,
      updateQuantity,
      refreshCart,
      clearCart,
      checkoutCart,
      processPendingAddToCart,
    ]
  );

  return (
    <CartContext.Provider value={value}>
      {children}
    </CartContext.Provider>
  );
}

export function useCart() {
  const ctx = useContext(CartContext);
  if (!ctx) {
    throw new Error('useCart must be used within CartProvider');
  }
  return ctx;
}

export default CartContext;
